import type { ClientMessage, CreateRoomResponse, MatchmakeResponse, PlayerInput, PlayerLoadout, PlayerProfile, RoomConfig, RoomPublicState, ServerMessage } from "../shared/types";

export type ConnectionStatus = "idle" | "connecting" | "open" | "closed" | "error";
export type LeaderboardPeriod = "daily" | "weekly" | "all";

export interface LeaderboardResponse {
  period: LeaderboardPeriod;
  players: PlayerProfile[];
}

export interface NetworkHandlers {
  onMessage(message: ServerMessage): void;
  onStatus(status: ConnectionStatus, detail?: string): void;
  onPing(latencyMs: number): void;
}

export class MultiplayerClient {
  private socket: WebSocket | null = null;
  private pingTimer: number | null = null;
  private lastPingSentAt = 0;

  constructor(private readonly handlers: NetworkHandlers) {}

  async createProfileSession(name: string, profileId?: string | null): Promise<PlayerProfile> {
    const response = await fetch("/api/profiles/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, profileId })
    });
    if (!response.ok) {
      throw new Error(await responseError(response, "Profile session failed"));
    }
    return (await response.json()) as PlayerProfile;
  }

  async createRoom(name: string, profileId?: string | null, config?: Partial<RoomConfig>): Promise<CreateRoomResponse> {
    const response = await fetch("/api/rooms", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, profileId, config })
    });
    if (!response.ok) {
      throw new Error(await responseError(response, "Room creation failed"));
    }
    return (await response.json()) as CreateRoomResponse;
  }

  async getRoom(roomId: string): Promise<RoomPublicState> {
    const response = await fetch(`/api/rooms/${encodeURIComponent(roomId)}`);
    if (!response.ok) {
      throw new Error(await responseError(response, "Room lookup failed"));
    }
    return (await response.json()) as RoomPublicState;
  }

  async requestMatch(name: string, profileId?: string | null): Promise<MatchmakeResponse> {
    const response = await fetch("/api/matchmake", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, profileId })
    });
    if (!response.ok) {
      throw new Error(await responseError(response, "Matchmaking failed"));
    }
    return (await response.json()) as MatchmakeResponse;
  }

  async pollMatch(ticketId: string): Promise<MatchmakeResponse> {
    const response = await fetch(`/api/matchmake/${encodeURIComponent(ticketId)}`);
    if (!response.ok) {
      throw new Error(await responseError(response, "Matchmaking ticket failed"));
    }
    return (await response.json()) as MatchmakeResponse;
  }

  async cancelMatch(ticketId: string): Promise<void> {
    const response = await fetch(`/api/matchmake/${encodeURIComponent(ticketId)}`, { method: "DELETE" });
    if (!response.ok && response.status !== 404 && response.status !== 410) {
      throw new Error(await responseError(response, "Cancel matchmaking failed"));
    }
  }

  async getLeaderboard(period: LeaderboardPeriod): Promise<LeaderboardResponse> {
    const response = await fetch(`/api/leaderboards/${encodeURIComponent(period)}`);
    if (!response.ok) {
      throw new Error(await responseError(response, "Leaderboard failed"));
    }
    return (await response.json()) as LeaderboardResponse;
  }

  connect(roomId: string, name: string, token?: string, profileId?: string | null, spectate = false): void {
    this.disconnect(false);
    this.handlers.onStatus("connecting");

    const wsProtocol = window.location.protocol === "https:" ? "wss:" : "ws:";
    const url = `${wsProtocol}//${window.location.host}/ws/${encodeURIComponent(roomId)}`;
    const socket = new WebSocket(url);
    this.socket = socket;

    this.socket.addEventListener("open", () => {
      if (this.socket !== socket) return;
      this.handlers.onStatus("open");
      this.send(spectate ? { type: "spectate" } : { type: "join", name, token, profileId: profileId ?? undefined });
      this.startPing();
    });

    this.socket.addEventListener("message", (event) => {
      if (this.socket !== socket) return;
      const message = safeParseMessage(event.data);
      if (!message) return;
      if (message.type === "pong") {
        this.handlers.onPing(Math.max(0, performance.now() - this.lastPingSentAt));
      }
      this.handlers.onMessage(message);
    });

    this.socket.addEventListener("close", () => {
      if (this.socket !== socket) return;
      this.stopPing();
      this.socket = null;
      this.handlers.onStatus("error", "Connection closed");
    });

    this.socket.addEventListener("error", () => {
      if (this.socket !== socket) return;
      this.handlers.onStatus("error", "WebSocket error");
    });
  }

  disconnect(leave = true): void {
    this.stopPing();
    if (this.socket && this.socket.readyState < WebSocket.CLOSING) {
      if (leave) this.send({ type: "leave" });
      this.socket.close(1000, "Client navigation");
    }
    this.socket = null;
    this.handlers.onStatus("closed");
  }

  ready(ready: boolean): void {
    this.send({ type: "ready", ready });
  }

  setLoadout(loadout: PlayerLoadout): void {
    this.send({ type: "set_loadout", loadout });
  }

  chat(message: string): void {
    this.send({ type: "chat", message });
  }

  requestRematch(): void {
    this.send({ type: "request_rematch" });
  }

  spectate(): void {
    this.send({ type: "spectate" });
  }

  input(input: PlayerInput): void {
    this.send({ type: "input", input });
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = window.setInterval(() => {
      this.lastPingSentAt = performance.now();
      this.send({ type: "ping", sentAt: this.lastPingSentAt });
    }, 2000);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      window.clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private send(message: ClientMessage): void {
    if (!this.socket || this.socket.readyState !== WebSocket.OPEN) return;
    this.socket.send(JSON.stringify(message));
  }
}

function safeParseMessage(value: unknown): ServerMessage | null {
  if (typeof value !== "string") return null;
  try {
    return JSON.parse(value) as ServerMessage;
  } catch {
    return null;
  }
}

async function responseError(response: Response, fallback: string): Promise<string> {
  try {
    const body = (await response.json()) as { error?: string; retryAfterMs?: number };
    const retry = body.retryAfterMs ? ` Retry in ${Math.ceil(body.retryAfterMs / 1000)}s.` : "";
    return `${body.error ?? fallback} (${response.status}).${retry}`;
  } catch {
    return `${fallback} (${response.status})`;
  }
}
